import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useReactFlow } from 'reactflow';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Skeleton,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/lab';
import {
    selectDevicesAvailableForSchemes,
    selectGetDevicesAvailableForSchemesStatus,
} from '@pages/schemes/store/selectors';
import { getDevicesAvailableForSchemesRequest } from '@pages/schemes/store';
import { NODE_DEFAULT_PARAMS, NODE_DEFAULT_PORTS, ZONES_LIST } from '@pages/schemes/constants';
import { generateUniqueStringKey } from '@shared/utils/utilsOld';
import { RequestStatusEnum } from '@shared/types';
import useDeleteNode from '@pages/schemes/hooks/useDeleteNode';

export const makeNodeUnconnectedToHwId = (hwId) => (node) => {
    const { connectedHwIds, ...otherNodeData } = node;

    if (!connectedHwIds?.length) {
        return node;
    } else {
        const index = connectedHwIds.findIndex((connectedHwId) => connectedHwId === hwId);
        connectedHwIds.splice(index, 1);
        return { ...otherNodeData, deletable: connectedHwIds.length === 0, connectedHwIds };
    }
};

const NodeTypeSelect = ({ node, hwId, setHwId, handleAddPort, handleRemovePort }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const reactFlow = useReactFlow();
    const devicesAvailableForSchemes = useSelector(selectDevicesAvailableForSchemes);
    const getDevicesAvailableForSchemesStatus = useSelector(selectGetDevicesAvailableForSchemesStatus);
    const [isHw, setIsHw] = useState(Boolean(hwId));
    const { removeUnusedZonesAndEdges } = useDeleteNode();

    const hwIdsUsedInScheme = useMemo(() => {
        const { getNodes } = reactFlow;
        const nodes = getNodes();

        return Array.from(
            nodes.reduce((acc, n) => {
                if (n.data.hw_id && n.id !== node.id) {
                    acc.add(n.data.hw_id);
                    return acc;
                }

                return acc;
            }, new Set()),
        );
    }, [node.id, reactFlow]);

    useEffect(() => {
        dispatch(getDevicesAvailableForSchemesRequest());
    }, [dispatch]);

    const handleClearNodeConnections = () => {
        const { setEdges } = reactFlow;
        setEdges((edges) => edges.filter((e) => e.source !== node.id));
    };

    const handleClearExplicitCreatedPorts = () => {
        for (const port of node.data.ports) {
            if (port.name === 'port0') {
                continue;
            }

            handleRemovePort(port.name);
        }
    };

    const handleClearHWExplicitCreatedInstances = () => {
        handleClearNodeConnections();
        handleClearExplicitCreatedPorts();
        removeUnusedZonesAndEdges(node);
    };

    const handleChangeNodeType = (event) => {
        const newIsHw = event.target.value === 'true';
        setIsHw(newIsHw);

        if (!newIsHw) {
            setHwId(null);
            handleClearHWExplicitCreatedInstances();
        }
    };

    const handleCreateHWExplicitCreatedInstances = (newHwId) => {
        const { getNodes, setNodes } = reactFlow;
        const hwObject = devicesAvailableForSchemes.find((device) => device.pk === newHwId);
        let createdZoneNodes = [];

        if (!hwObject) {
            return;
        }

        function addMissingZones() {
            const existingNodes = getNodes();
            const existingZones = existingNodes.filter((node) => ZONES_LIST.includes(node.type));
            const neededZones = Object.values(hwObject.zone_connections);

            let zonesToCreate = new Set();
            let zonesIdsToReuse = new Set();

            for (const neededZone of neededZones) {
                const zoneToReuse = existingZones.find(
                    (existingZone) => existingZone.type === neededZone && existingZone.data.type === 'static',
                );

                if (!zoneToReuse) {
                    zonesToCreate.add(neededZone);
                } else {
                    zonesIdsToReuse.add(zoneToReuse.id);
                }
            }

            const { x, y } = node.position;

            const nodesToCreate = Array.from(zonesToCreate).map((zoneToCreate, index) => ({
                id: generateUniqueStringKey(zoneToCreate),
                type: zoneToCreate,
                position: {
                    x: x + 150,
                    y: y + index * 150 - 100,
                },
                data: {
                    ports: structuredClone(NODE_DEFAULT_PORTS[zoneToCreate]),
                    rotation: 0,
                    type: 'static',
                    index: 1,
                    ...NODE_DEFAULT_PARAMS[zoneToCreate],
                },
                connectedHwIds: [newHwId],
                deletable: false,
            }));

            createdZoneNodes = nodesToCreate; // setState update is async, but we need created nodes in getNearestZoneNode function
            setNodes((prev) => {
                return [
                    ...prev.map((existingNode) =>
                        zonesIdsToReuse.has(existingNode.id)
                            ? {
                                  ...existingNode,
                                  connectedHwIds: existingNode.connectedHwIds?.length
                                      ? [...existingNode.connectedHwIds, newHwId]
                                      : [newHwId],
                                  deletable: false,
                              }
                            : existingNode,
                    ),
                    ...nodesToCreate,
                ];
            });
        }

        function addMissingPorts() {
            const neededPorts = Object.keys(hwObject.zone_connections);
            const lastExistingPortNumber = Number(structuredClone(node.data).ports.at(-1).name.replace('port', ''));
            const lastNeededPortNumber = neededPorts.reduce((max, cur) => {
                const num = parseInt(cur.replace('port', ''), 10);
                return num > max ? num : max;
            }, 0);

            for (let i = lastExistingPortNumber; i < lastNeededPortNumber; i++) {
                const portName = `port${i + 1}`;
                const portPayload = { name: portName, ips: 1 };

                if (!neededPorts.includes(portName)) {
                    portPayload.isConnectable = false;
                }
                handleAddPort(portPayload);
            }
        }

        function addMissingConnections() {
            const { addEdges, getNodes } = reactFlow;

            function getNearestZoneNode(nodes) {
                if (!nodes.length) {
                    return null;
                }

                return nodes.reduce(
                    (currentMin, zoneNode) => {
                        const dx = zoneNode.position.x - node.position.x;
                        const dy = zoneNode.position.y - node.position.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);

                        if (dist < currentMin.dist) {
                            return { node: zoneNode, dist };
                        }

                        return currentMin;
                    },
                    { node: nodes[0], dist: Infinity },
                ).node;
            }

            for (const [portName, zoneType] of Object.entries(hwObject.zone_connections)) {
                const zoneDefaultPort = 'port0';
                const source = node.id;
                const sourceHandle = portName;

                const allNodes = [...getNodes(), ...createdZoneNodes];
                const suitableZoneNodes = allNodes.filter((node) => node.type === zoneType);
                const nearestZoneNode = getNearestZoneNode(suitableZoneNodes);

                if (!nearestZoneNode) {
                    return;
                }

                const target = suitableZoneNodes.length > 1 ? nearestZoneNode.id : suitableZoneNodes[0].id;
                const targetHandle = zoneDefaultPort;

                addEdges({
                    id: `${source}-${target}-${portName}-${zoneDefaultPort}`,
                    source,
                    sourceHandle,
                    target,
                    targetHandle,
                    deletable: false,
                });
            }
        }

        addMissingZones();
        addMissingPorts();
        handleClearNodeConnections();
        addMissingConnections();
    };

    const handleChangeHwId = (event) => {
        const newHwId = event.target.value;
        setHwId(newHwId);
        handleClearHWExplicitCreatedInstances();
        setTimeout(handleCreateHWExplicitCreatedInstances, 0, newHwId);
    };

    const handleClearHwId = () => {
        setHwId(null);
        handleClearHWExplicitCreatedInstances();
    };

    return (
        <Box sx={{ pl: 2, pt: 1, pr: 2 }}>
            <FormControl>
                <RadioGroup value={isHw} onChange={handleChangeNodeType} name="node-type">
                    <FormControlLabel value={false} control={<Radio />} label="Virtual" />
                    <FormControlLabel value={true} control={<Radio />} label="Physical device" />
                </RadioGroup>
            </FormControl>
            {isHw && (
                <Box>
                    <FormControl
                        sx={{ mt: 1 }}
                        fullWidth
                        size="small"
                        error={getDevicesAvailableForSchemesStatus === RequestStatusEnum.Error}
                    >
                        <InputLabel id="node-nw-id">Device</InputLabel>
                        <Select
                            renderValue={
                                getDevicesAvailableForSchemesStatus === RequestStatusEnum.Loading
                                    ? () => <Skeleton animation="wave" variant="text" width={'100%'} />
                                    : undefined
                            }
                            labelId="node-nw-id"
                            value={hwId}
                            label="Device"
                            onChange={handleChangeHwId}
                            endAdornment={
                                hwId &&
                                getDevicesAvailableForSchemesStatus === RequestStatusEnum.Success && (
                                    <InputAdornment position="start" sx={{ mr: 2 }}>
                                        <IconButton size="small" onClick={handleClearHwId}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        >
                            {devicesAvailableForSchemes
                                .filter(
                                    (device) =>
                                        !hwIdsUsedInScheme.includes(device.pk) &&
                                        Object.keys(device.zone_connections || {}).length,
                                )
                                .map((device) => (
                                    <MenuItem
                                        key={device.pk}
                                        value={device.pk}
                                    >{`${device.name} ${device.office}`}</MenuItem>
                                ))}
                        </Select>
                        {getDevicesAvailableForSchemesStatus === RequestStatusEnum.Error && (
                            <FormHelperText>{`${t('error_while')} ${t('fetching')} ${t('devices_r')}`}</FormHelperText>
                        )}
                    </FormControl>
                </Box>
            )}
            {isHw && !hwId && reactFlow.getEdges().find((edge) => edge.source === node.id) && (
                <Alert sx={{ mb: 1, maxWidth: '300px' }} severity="warning">
                    {t('connections_lost_message')}
                </Alert>
            )}
        </Box>
    );
};

export default NodeTypeSelect;
